import React, { useState, useEffect } from 'react';
import './FlashcardViewer.css';

/**
 * FlashcardViewer Component
 * 
 * Displays flashcards for a specific training module with flip animation.
 * Features:
 * - Card flip on click (question → answer)
 * - Previous/Next navigation
 * - Progress indicator (card X of Y)
 * - Keyboard navigation (← → arrows, Space to flip)
 * - Auto-fetch flashcards from API
 */
const FlashcardViewer = ({ moduleId }) => {
  const [flashcards, setFlashcards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch flashcards on mount
  useEffect(() => {
    fetchFlashcards();
  }, [moduleId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === ' ') {
        e.preventDefault();
        handleFlip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, flashcards.length]);

  const fetchFlashcards = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(
        `${apiUrl}/learning/flashcards/${moduleId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        setFlashcards(data.flashcards || []);
        setCurrentIndex(0);
        setIsFlipped(false);
      } else {
        setError(data.message || 'Failed to load flashcards');
      }
    } catch (err) {
      console.error('Error fetching flashcards:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleNext = () => {
    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  if (loading) {
    return (
      <div className="flashcard-viewer">
        <div className="loading-flashcards">
          <div className="spinner"></div>
          <p>Loading flashcards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flashcard-viewer">
        <div className="error-flashcards">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchFlashcards} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (flashcards.length === 0) {
    return (
      <div className="flashcard-viewer">
        <div className="empty-flashcards">
          <span className="empty-icon">📚</span>
          <p>No flashcards available for this module yet.</p>
          <p className="empty-hint">
            Flashcards are created from key concepts and questions in the training content.
          </p>
        </div>
      </div>
    );
  }

  const currentCard = flashcards[currentIndex];

  return (
    <div className="flashcard-viewer">
      <div className="flashcard-header">
        <h3>📇 Study Flashcards</h3>
        <div className="flashcard-progress">
          <span className="current">{currentIndex + 1}</span>
          <span className="separator">/</span>
          <span className="total">{flashcards.length}</span>
        </div>
      </div>

      <div className="flashcard-container">
        <div 
          className={`flashcard ${isFlipped ? 'flipped' : ''}`}
          onClick={handleFlip}
        >
          <div className="flashcard-front">
            <div className="card-label">Question</div>
            <div className="card-content">
              {currentCard.question}
            </div>
            <div className="flip-hint">
              Click card or press SPACE to reveal answer
            </div>
          </div>
          
          <div className="flashcard-back">
            <div className="card-label">Answer</div>
            <div className="card-content">
              {currentCard.answer}
            </div>
            <div className="flip-hint">
              Click card or press SPACE to flip back
            </div>
          </div>
        </div>
      </div>

      <div className="flashcard-controls">
        <button
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          className="nav-btn prev-btn"
          title="Previous card (Left Arrow)"
        >
          ← Previous
        </button>

        <button
          onClick={handleFlip}
          className="flip-btn"
          title="Flip card (Space)"
        >
          {isFlipped ? '🔄 Flip to Question' : '🔄 Flip to Answer'}
        </button>

        <button
          onClick={handleNext}
          disabled={currentIndex === flashcards.length - 1}
          className="nav-btn next-btn"
          title="Next card (Right Arrow)"
        >
          Next →
        </button>
      </div>

      <div className="keyboard-hints">
        <span>← → Navigate</span>
        <span>Space Flip</span>
      </div>
    </div>
  );
};

export default FlashcardViewer;
